import * as React from 'react';

import {Field} from 'redux-form';
// @ts-expect-error
import type {FieldArrayProps} from 'redux-form';

import {Button} from 'shells/button/button';
import {Line} from 'shells/divider/line';
import {AddAnotherButton} from 'shells/form/sidebar-fields/add-another-button';
import {nonNull} from 'shells/form/validation/non-null';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';

import type {TagType} from 'nutshell-graphql-types';

import {SelectFieldCreateable} from '../../select/select-field-createable';

import {getAddAnotherText, getComponent, idRegex} from './createable-field-helpers';

import styles from './forms.module.css';

type Props = FieldArrayProps & {
    setNewItem: (arg1?: string | null | undefined) => void;
    setOptions: (arg1: any[]) => void;
    options: any[];
    refetch: () => Promise<any>;
    shouldAnd?: boolean;
    tagType?: TagType;
    isLoading: boolean;
    type: 'tags' | 'audiences' | 'contacts' | 'accounts';
    autoFocus?: boolean;
    isRequired?: boolean;
    isCreateable?: boolean;
    onInputChange?: (newInputValue: string) => void;
};

export function CreateableField(props: Props) {
    const {
        fields,
        setNewItem,
        setOptions,
        options,
        refetch,
        shouldAnd,
        isLoading,
        type,
        tagType,
        autoFocus,
        isRequired = true,
        isCreateable = true,
    } = props;

    // We show the add another button once a field has been added
    const shouldShowAddAnother = fields.get(fields.length - 1);

    // We show the cancel button if there is already at least one selected field but they haven't added the next one yet
    const shouldShowCancel = !fields.get(fields.length - 1) && fields.length > 1;

    return (
        <StackLegacy spacing={8}>
            {/* @ts-expect-error */}
            {fields.map((member, index) => (
                <div key={member}>
                    <Field
                        name={member}
                        component={SelectFieldCreateable}
                        autoFocus={autoFocus}
                        isCreateable={isCreateable}
                        onRemove={() => {
                            fields.remove(index);
                        }}
                        props={{
                            // @ts-expect-error
                            onChange: (newValue) => {
                                setNewItem(newValue.value);
                            },
                            // @ts-expect-error
                            onInputChange: (newValue) => {
                                if (props.onInputChange) {
                                    props.onInputChange(newValue);
                                }
                            },
                        }}
                        createComponent={getComponent(type)}
                        optionFields={options}
                        selectedOptions={fields.getAll()}
                        validate={isRequired ? [nonNull, isApiId] : [isApiId]}
                        isLoading={isLoading}
                        refetch={refetch}
                        // @ts-expect-error
                        updateOptions={(newOption) => setOptions(options.concat(newOption))}
                        tagType={tagType}
                        shouldAnd={shouldAnd}
                    />
                    {index !== fields.length - 1 ? (
                        <Line text={shouldAnd ? 'and' : 'or'} />
                    ) : undefined}
                </div>
            ))}
            {shouldShowAddAnother ? (
                <AddAnotherButton
                    text={getAddAnotherText(type)}
                    onClick={() => {
                        fields.push(null);
                    }}
                />
            ) : undefined}
            {shouldShowCancel ? (
                <div className={styles['cancel-button']}>
                    <Button onClick={() => fields.remove(-1)}>Cancel</Button>
                </div>
            ) : undefined}
        </StackLegacy>
    );
}

// @ts-expect-error
const isApiId = (value) => {
    return value && !idRegex.test(value) ? 'Creating item' : undefined;
};
